import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/auth/useAuth';

/**
 * Props for the AdminRoute component
 * @interface AdminRouteProps
 */
interface AdminRouteProps {
  /** Child components to render if the user is authenticated and an admin */
  children: React.ReactNode;
}

/**
 * A component that ensures the route is accessible only to authenticated admin users
 * @function AdminRoute
 * @param {AdminRouteProps} props - The props for the component
 * @returns JSX element or redirects to login or home if conditions are not met
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  /**
   * Redirects to login if the user is not authenticated
   */
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  /**
   * Redirects to home if the user is authenticated but not an admin
   */
  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  /**
   * Returns the child components if the user is authenticated and an admin
   */
  return <>{children}</>;
};

export default AdminRoute;
